import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';
import React, { useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
  AutocompleteInput,
  Datagrid,
  FunctionField,
  NumberField,
  ReferenceInput,
  SelectInput,
  TextField,
  useListContext,
} from 'react-admin';
import { ReportList } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import RequiredChain from '../../components/RequiredChain';
import { getChainId, getPlaceId } from '../../lib';
import { filterToQuery, formatNumber } from '../../utils';

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold',
};

const printCell = () => ({
  '@media print': { '& td': { padding: '2px 8px' } },
});

const currencyOptions = { style: 'currency' as const, currency: 'BRL' };

// ─── Summary ────────────────────────────────

const SummaryData = () => {
  const { data } = useListContext();
  if (!data?.[0]?.summaryData) return null;
  const s = data[0].summaryData;

  const summaryRows = [
    { id: 'total', label: 'Total a Pagar', value: s.totalPrize || 0 },
    { id: 'days', label: 'Dias-Meta Batidos', value: s.totalDaysAchieved || 0 },
    { id: 'attendants', label: 'Frentistas', value: s.totalAttendants || 0 },
  ];

  return (
    <>
      <Typography sx={titleSx}>Resumo Geral</Typography>
      <Datagrid bulkActionButtons={false} data={summaryRows} total={summaryRows.length} rowSx={printCell}>
        <TextField source="label" label="" sortable={false} />
        <FunctionField
          label="Valor"
          textAlign="center"
          sortable={false}
          render={(record: any) =>
            record.id === 'total'
              ? formatNumber(record.value, currencyOptions)
              : record.value
          }
        />
      </Datagrid>
    </>
  );
};

// ─── Boards Summary ─────────────────────────

const BoardsSummary = () => {
  const { data } = useListContext();
  if (!data?.[0]?.summaryData?.boardsSummary?.length) return null;
  const boards = [...data[0].summaryData.boardsSummary]
    .sort((a: any, b: any) => (b.totalPrize || 0) - (a.totalPrize || 0))
    .map((b: any, i: number) => ({ ...b, id: i }));

  return (
    <>
      <Typography sx={titleSx}>Resumo por Quadro</Typography>
      <Datagrid bulkActionButtons={false} data={boards} total={boards.length} rowSx={printCell}>
        <TextField source="boardCode" label="Código" sortable={false} />
        <TextField source="boardName" label="Quadro" sortable={false} />
        <NumberField source="totalPrize" label="Total a Pagar" options={currencyOptions} textAlign="center" sortable={false} />
        <NumberField source="daysAchieved" label="Metas Atingidas" textAlign="center" sortable={false} />
        <NumberField source="attendantsAchieved" label="Frentistas" textAlign="center" sortable={false} />
      </Datagrid>
    </>
  );
};

// ─── Attendant Row (expandable) ─────────────

const headerSx = { fontWeight: 'bold', color: 'white', bgcolor: 'primary.main', py: 0.5, px: 1 };
const cellSx = { py: 0.5, px: 1, fontSize: '0.8rem' };
const subHeaderSx = { fontWeight: 'bold', bgcolor: 'grey.100', py: 0.5, px: 1, fontSize: '0.75rem' };
const subCellSx = { py: 0.5, px: 1, fontSize: '0.75rem', color: 'text.secondary' };

const AttendantRow = ({ row }: { row: any }) => {
  const [open, setOpen] = useState(false);
  const hasMultipleBoards = row.boards?.length > 1;

  return (
    <>
      <TableRow sx={{ '& td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell sx={cellSx}>
          {hasMultipleBoards && (
            <IconButton size="small" onClick={() => setOpen(o => !o)} sx={{ p: 0, mr: 0.5 }}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
          {row.attendantName}
        </TableCell>
        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>{row.daysWorked}</TableCell>
        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>{row.daysAchieved}</TableCell>
        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
          {hasMultipleBoards ? '—' : `${row.achievementRate || 0}%`}
        </TableCell>
        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
          {formatNumber(row.totalPrize || 0, currencyOptions)}
        </TableCell>
      </TableRow>
      {hasMultipleBoards && (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ bgcolor: 'grey.50', px: 4, py: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={subHeaderSx}>Quadro</TableCell>
                      <TableCell sx={{ ...subHeaderSx, textAlign: 'center' }}>Metas Atingidas</TableCell>
                      <TableCell sx={{ ...subHeaderSx, textAlign: 'center' }}>%</TableCell>
                      <TableCell sx={{ ...subHeaderSx, textAlign: 'center' }}>Total a Receber</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(row.boards || []).map((b: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell sx={subCellSx}>{b.boardCode} — {b.boardName}</TableCell>
                        <TableCell sx={{ ...subCellSx, textAlign: 'center' }}>{b.daysAchieved}</TableCell>
                        <TableCell sx={{ ...subCellSx, textAlign: 'center' }}>
                          {row.daysWorked > 0 ? `${Math.round((b.daysAchieved / row.daysWorked) * 100)}%` : '—'}
                        </TableCell>
                        <TableCell sx={{ ...subCellSx, textAlign: 'center' }}>
                          {formatNumber(b.prize || 0, currencyOptions)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ─── Detail ─────────────────────────────────

const AttendantDetail = () => {
  const { data } = useListContext();
  if (!data?.[0]?.data) return null;
  const rows = data[0].data;

  return (
    <>
      <Typography sx={titleSx}>Detalhe por Frentista</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>Frentista</TableCell>
            <TableCell sx={{ ...headerSx, textAlign: 'center' }}>Dias Trabalhados</TableCell>
            <TableCell sx={{ ...headerSx, textAlign: 'center' }}>Metas Atingidas</TableCell>
            <TableCell sx={{ ...headerSx, textAlign: 'center' }}>%</TableCell>
            <TableCell sx={{ ...headerSx, textAlign: 'center' }}>Total a Receber</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: any) => <AttendantRow key={row.id} row={row} />)}
        </TableBody>
      </Table>
    </>
  );
};

// ─── Filters ────────────────────────────────

const BoardFilter = (_props: { alwaysOn?: boolean }) => {
  const chainId = getChainId();
  const stationId = useWatch({ name: 'stationIds' });
  const filter: any = { isDisabled: false };
  if (chainId) filter.chainId = chainId;
  if (stationId) filter.stationId = stationId;

  return (
    <ReferenceInput
      source="attendantGoalBoardId"
      reference="attendant-goal-boards"
      sort={{ field: 'name', order: 'ASC' }}
      perPage={100}
      filter={filter}
      alwaysOn
    >
      <SelectInput
        label="Quadro"
        optionText={(record: any) => record ? `${record.code} - ${record.name}` : ''}
        emptyText="Todos"
        sx={{ minWidth: 220 }}
      />
    </ReferenceInput>
  );
};

const getListFilters = () => {
  const placeId = getPlaceId();
  const chainId = getChainId();

  return [
    <BetweenDatesInput alwaysOn />,
    ...(!placeId ? [
      <ReferenceInput
        source="stationIds"
        reference="stations"
        sort={{ field: 'name', order: 'ASC' }}
        perPage={9999}
        filter={chainId ? { chainId } : {}}
        alwaysOn
      >
        <AutocompleteInput
          label="Posto"
          filterToQuery={filterToQuery}
          sx={{ minWidth: 200 }}
          optionText="name"
        />
      </ReferenceInput>,
    ] : []),
    <BoardFilter key="boardFilter" alwaysOn />,
  ];
};

// ─── Exporter ───────────────────────────────

const exporter = async (data: any[]) => {
  if (!data?.[0]?.data) return;
  const rows = data[0].data;

  return rows.map((r: any) => ({
    frentista: r.attendantName,
    dias_trabalhados: r.daysWorked,
    dias_batidos: r.daysAchieved,
    percentual: `${r.achievementRate}%`,
    total_a_receber: r.totalPrize,
  }));
};

// ─── Main ───────────────────────────────────

const ReportAttendantGoalAchievement = (props: any) => {
  return (
    <Permission permission={PERMISSIONS.ATTENDANT_GOAL_ACHIEVEMENT_REPORT}>
      <RequiredChain>
        <ReportList periodRequired
          {...props}
          title="Metas Batidas"
          resource="chains/chainId/reports/attendant-goal-achievement"
          filters={getListFilters()}
          filterDefaultValues={{
            from: moment().startOf('month').toISOString(),
            to: moment().endOf('day').toISOString(),
          }}
          sort={{ field: 'totalPrize', order: 'DESC' }}
          pagination={<ListPagination />}
          customExporter={exporter}
          titleOnPrint="Relatório de Metas Batidas"
          bulkActionButtons={false}
        >
          <Box>
            <SummaryData />
            <BoardsSummary />
            <AttendantDetail />
          </Box>
        </ReportList>
      </RequiredChain>
    </Permission>
  );
};

export default ReportAttendantGoalAchievement;
